import { Alert, AlertDescription } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"
import {
  setOpenModalUnavailabilityCreate,
  setOpenModalUnavailabilityDetails,
  UnavailabilityData,
} from "@/store/modalsReducers/modalSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import {
  Button,
  Select,
  UnavailabilityCreateModal,
  UnavailabilityDetailsModal,
  UnavailabilityListItem,
} from "@okeys/shared-react-components"
import {
  AdminShop,
  Manager,
  Prestatere,
  UnavailabilityShopTable,
} from "@okeys/shared-utils"
import { PlusIcon } from "lucide-react"
import { useState } from "react"
import { useShopDataQuery } from "../../query/shopDataQuery"
import {
  useAddUnavailabilityPrestatereMutation,
  useUnavailabilityPrestatereQuery,
} from "../../query/unavailabilityPrestatereQuery"
import {
  useAddUnavailabilityShopMutation,
  useUnavailabilityShopQuery,
} from "../../query/unavailabilityShopQuery"

// Local type that matches the modal component's type
interface UnavailabilityCreateDataLocal {
  type: "prestataire" | "shop"
  prestatereId?: string
  startDate: string
  endDate: string
  reason: string
  status?: "pending" | "accepted" | "rejected"
}

// Type temporaire pour UnavailabilityShopData jusqu'à la mise à jour du package
interface UnavailabilityShopData {
  id: string
  shopId: string
  triggerEmail: string
  startDate: string
  endDate: string
  processingDate: string
  reason?: string
  type: "shop"
}

export const Unavailability = () => {
  const [activeTab, setActiveTab] = useState("prestataires")
  const dispatch = useAppDispatch()
  const modalCreateState = useAppSelector(
    (state) => state.modal.openModalUnavailabilityCreate
  )
  const modalDetailsState = useAppSelector(
    (state) => state.modal.openModalUnavailabilityDetails
  )

  // Queries pour récupérer les données
  const {
    unavailabilityPrestatere,
    isLoadingUnavailabilityPrestatere,
    errorUnavailabilityPrestatere,
  } = useUnavailabilityPrestatereQuery()

  const {
    unavailabilityShop,
    isLoadingUnavailabilityShop,
    errorUnavailabilityShop,
  } = useUnavailabilityShopQuery()

  const { shopData } = useShopDataQuery()

  // Mutations pour ajouter des indisponibilités
  const addUnavailabilityPrestatereMutation =
    useAddUnavailabilityPrestatereMutation()
  const addUnavailabilityShopMutation = useAddUnavailabilityShopMutation()

  const isLoading =
    isLoadingUnavailabilityPrestatere || isLoadingUnavailabilityShop

  const getPrestataireName = (prestatereId: string): string => {
    // Chercher dans les prestataires
    const prestatere = shopData?.prestateres?.find(
      (p: Prestatere) => p.id === prestatereId
    )
    if (prestatere) {
      return `${prestatere.givenName} ${prestatere.familyName}`
    }

    // Chercher dans les managers
    const manager = shopData?.managers?.find(
      (m: Manager) => m.id === prestatereId
    )
    if (manager) {
      return `${manager.givenName} ${manager.familyName}`
    }

    // Chercher dans les admins
    const admin = shopData?.admins?.find(
      (a: AdminShop) => a.id === prestatereId
    )
    if (admin) {
      return `${admin.givenName} ${admin.familyName}`
    }

    return `Prestataire ${prestatereId}`
  }

  const getPrestatereEmail = (prestatereId: string): string => {
    // Chercher dans les prestataires
    const prestatere = shopData?.prestateres?.find(
      (p: Prestatere) => p.id === prestatereId
    )
    if (prestatere) {
      return prestatere.email
    }

    // Chercher dans les managers
    const manager = shopData?.managers?.find(
      (m: Manager) => m.id === prestatereId
    )
    if (manager) {
      return manager.email
    }

    // Chercher dans les admins
    const admin = shopData?.admins?.find(
      (a: AdminShop) => a.id === prestatereId
    )
    if (admin) {
      return admin.email
    }

    return ""
  }

  const handleEditShop = (unavailability: UnavailabilityShopTable) => {
    const data: UnavailabilityData = {
      ...unavailability,
      type: "shop",
    }
    dispatch(
      setOpenModalUnavailabilityDetails({
        isOpen: true,
        unavailability: data,
      })
    )
  }

  const handleEditPrestataire = (unavailability: any) => {
    const data: UnavailabilityData = {
      ...unavailability,
      type: "prestataire",
      prestataireName: getPrestataireName(unavailability.prestatereId),
    }
    dispatch(
      setOpenModalUnavailabilityDetails({
        isOpen: true,
        unavailability: data,
      })
    )
  }

  const handleCloseModal = () => {
    dispatch(setOpenModalUnavailabilityCreate({ isOpen: false }))
  }

  const handleSaveUnavailability = async (data: UnavailabilityCreateDataLocal) => {
    try {
      if (data.type === "prestataire" && data.prestatereId) {
        // Récupérer l'email du prestataire
        const emailPrestatere = getPrestatereEmail(data.prestatereId)

        // Ajouter une indisponibilité prestataire
        await addUnavailabilityPrestatereMutation.mutateAsync({
          prestatereId: data.prestatereId,
          startDate: data.startDate,
          endDate: data.endDate,
          reason: data.reason,
          status: data.status || "pending",
          emailPrestatere: emailPrestatere,
        })
      } else if (data.type === "shop") {
        // Ajouter une indisponibilité shop
        await addUnavailabilityShopMutation.mutateAsync({
          startDate: data.startDate,
          endDate: data.endDate,
          reason: data.reason,
          processingDate: new Date().toISOString(),
        })
      }

      handleCloseModal()
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'indisponibilité:", error)
      // L'erreur est déjà gérée par les mutations avec les snackbars
    }
  }

  const prestatereUnavailabilities = unavailabilityPrestatere || []
  const shopUnavailabilities = unavailabilityShop || []

  // Préparer la liste des prestataires avec leurs schedules pour le modal
  const prestataires = [
    ...(shopData?.prestateres?.map((p: Prestatere) => ({
      id: p.id,
      name: `${p.givenName} ${p.familyName}`,
      schedule: p.schedule,
    })) || []),
    ...(shopData?.managers
      ?.filter((m: Manager) => m.isPrestatere)
      ?.map((m: Manager) => ({
        id: m.id,
        name: `${m.givenName} ${m.familyName}`,
        schedule: m.schedule,
      })) || []),
    ...(shopData?.admins
      ?.filter((a: AdminShop) => a.isPrestatere)
      ?.map((a: AdminShop) => ({
        id: a.id,
        name: `${a.givenName} ${a.familyName}`,
        schedule: a.schedule,
      })) || []),
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-row gap-2 justify-between items-center">
        <Button
          onClick={() =>
            dispatch(
              setOpenModalUnavailabilityCreate({
                isOpen: true,
                defaultType: "prestataire",
              })
            )
          }
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Ajouter une indisponibilité
        </Button>
        {/* Contenu principal avec onglets */}
        <div className="mb-4">
          <Select
            value={activeTab}
            onChange={(value) => setActiveTab(value)}
            options={[
              {
                value: "prestataires",
                label: `Prestataires (${prestatereUnavailabilities.length})`,
              },
              {
                value: "boutique",
                label: `Boutique (${shopUnavailabilities.length})`,
              },
            ]}
            placeholder="Sélectionner un type"
            className="w-[250px]"
          />
        </div>
      </div>

      <div className="space-y-4 mt-6">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : activeTab === "prestataires" ? (
          <>
            {errorUnavailabilityPrestatere ? (
              <Alert variant="destructive">
                <AlertDescription>
                  Erreur lors du chargement des indisponibilités prestataires.
                </AlertDescription>
              </Alert>
            ) : prestatereUnavailabilities.length === 0 ? (
              <div className="text-center text-muted-foreground pt-20">
                Aucune indisponibilité de prestataire enregistrée.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {prestatereUnavailabilities.map((unavailability) => {
                  const data: UnavailabilityData = {
                    ...unavailability,
                    type: "prestataire",
                    prestataireName: getPrestataireName(
                      unavailability.prestatereId
                    ),
                  }
                  return (
                    <UnavailabilityListItem
                      key={unavailability.id}
                      unavailability={data as any}
                      onClick={() => handleEditPrestataire(unavailability)}
                    />
                  )
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {errorUnavailabilityShop ? (
              <Alert variant="destructive">
                <AlertDescription>
                  Erreur lors du chargement des fermetures boutique.
                </AlertDescription>
              </Alert>
            ) : shopUnavailabilities.length === 0 ? (
              <div className="text-center text-muted-foreground pt-20">
                Aucune fermeture de boutique enregistrée.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {shopUnavailabilities.map((unavailability) => {
                  const data: UnavailabilityShopData = {
                    ...unavailability,
                    type: "shop",
                  }
                  return (
                    <UnavailabilityListItem
                      key={unavailability.id}
                      unavailability={data}
                      onClick={() => handleEditShop(unavailability)}
                    />
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de création */}
      <UnavailabilityCreateModal
        open={modalCreateState.isOpen}
        onOpenChange={(open: boolean) =>
          dispatch(
            setOpenModalUnavailabilityCreate({
              isOpen: open,
              defaultType: modalCreateState.defaultType,
            })
          )
        }
        onSave={handleSaveUnavailability}
        onCancel={handleCloseModal}
        prestataires={prestataires}
        shopSchedule={shopData?.shopSchedule || undefined}
        defaultType={modalCreateState.defaultType || "prestataire"}
      />

      {/* Modal de détails/édition */}
      <UnavailabilityDetailsModal
        open={modalDetailsState.isOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            dispatch(
              setOpenModalUnavailabilityDetails({
                isOpen: false,
                unavailability: null,
              })
            )
          }
        }}
        unavailability={modalDetailsState.unavailability}
        onSave={(updatedUnavailability) => {
          // TODO: Implémenter la mutation pour mettre à jour l'indisponibilité
          console.log("Update unavailability:", updatedUnavailability)
          dispatch(
            setOpenModalUnavailabilityDetails({
              isOpen: false,
              unavailability: null,
            })
          )
        }}
        onDelete={(unavailability) => {
          // TODO: Implémenter la mutation pour supprimer l'indisponibilité
          console.log("Delete unavailability:", unavailability)
          dispatch(
            setOpenModalUnavailabilityDetails({
              isOpen: false,
              unavailability: null,
            })
          )
        }}
        onCancel={() => {
          dispatch(
            setOpenModalUnavailabilityDetails({
              isOpen: false,
              unavailability: null,
            })
          )
        }}
      />
    </div>
  )
}
